import React, { Suspense } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { SIGN_IN_CALLBACK_URL } from "shared-auth";
import { LaunchDarklyProvider } from "shared-services";
import {
  AnalyticsProvider,
  Authenticator,
  AuthProvider,
  ErrorTrackingProvider,
  Spinner,
  MaintenanceGate,
  TranslationContextProvider,
} from "component-library";
import {
  ADD_CREDIT_PATH,
  BASE_PATH,
  BASIC_DETAILS_PATH,
  CONTACT_ADD_PATH,
  CONTACT_HELP_PATH,
  CONTACT_NEW_PATH,
  CREDITS_PATH,
  EDIT_AUDIO_PATH,
  EDIT_CREDIT_PATH,
  EDIT_PHOTO_PATH,
  EDIT_VIDEO_PATH,
  LINK_TO_PROFILE_PATH,
  LOGOUT_PATH,
  MEDIA_ADD_PATH,
  MEDIA_PATH,
  MY_VIEW_PIN_PATH,
  PROFILE_CONTACT_PATH,
  SKILLS_PATH,
  SOCIAL_CONNECT_COMPLETE_PATH,
  SOCIAL_LINKS_PATH,
  VIEW_PIN_PATH,
} from "app/config/routes";
import {
  AddAgencyPage,
  AddMediaPage,
  AddNewAgencyPage,
  AgenciesHelpPage,
  AgenciesPage,
  BasicInfoPage,
  CreditsPage,
  EditAudioPage,
  EditCreditPage,
  EditVideoPage,
  GalleryPage,
  LinkToProfilePage,
  MediaPage,
  PageNotFound,
  SkillsPage,
  SocialMediaAuthComplete,
  SocialMediaPage,
} from "app/MasterPage/PagesList";
import Logout from "pages/AuthPages/Logout";
import App from "../../App";
import {
  agenciesHelpPageRestrictionLoader,
  basePathLoader,
  contactsRestrictionLoader,
  socialMediaPageRestrictionLoader,
  viewPinPageLoader,
  viewPinRedirectPageLoader,
} from "app/app-shell/Root/routeLoaders";
import Profile from "pages/Profile";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const AuthenticatorWithOutletWrapper = () => (
  <Authenticator>
    <Outlet />
  </Authenticator>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />}>
      <Route path={BASE_PATH}>
        <Route element={<AuthenticatorWithOutletWrapper />}>
          <Route index element={<Profile />} />

          {/* Profile sections */}
          <Route path={EDIT_PHOTO_PATH} element={<GalleryPage />} />
          <Route path={CREDITS_PATH} element={<CreditsPage />} />
          <Route path={EDIT_CREDIT_PATH} element={<EditCreditPage />} />
          <Route path={ADD_CREDIT_PATH} element={<EditCreditPage />} />
          <Route path={BASIC_DETAILS_PATH} element={<BasicInfoPage />} />
          <Route path={SKILLS_PATH} element={<SkillsPage />} />

          {/* Contacts / agencies */}
          <Route
            loader={contactsRestrictionLoader}
            errorElement={<PageNotFound />}
          >
            <Route path={PROFILE_CONTACT_PATH} element={<AgenciesPage />} />
            <Route path={CONTACT_ADD_PATH} element={<AddAgencyPage />} />
            <Route path={CONTACT_NEW_PATH} element={<AddNewAgencyPage />} />
            <Route
              path={CONTACT_HELP_PATH}
              element={<AgenciesHelpPage />}
              loader={agenciesHelpPageRestrictionLoader}
              errorElement={<PageNotFound />}
            />
          </Route>

          {/* Social links */}
          <Route
            path={SOCIAL_LINKS_PATH}
            element={<SocialMediaPage />}
            loader={socialMediaPageRestrictionLoader}
            errorElement={<PageNotFound />}
          />
          <Route path={LINK_TO_PROFILE_PATH} element={<LinkToProfilePage />} />

          {/* Media */}
          <Route path={MEDIA_ADD_PATH} element={<AddMediaPage />} />
          <Route path={MEDIA_PATH} element={<MediaPage />} />
          <Route path={EDIT_AUDIO_PATH} element={<EditAudioPage />} />
          <Route path={EDIT_VIDEO_PATH} element={<EditVideoPage />} />

          {/* Utility */}
          <Route path={LOGOUT_PATH} element={<Logout />} />
          <Route path={MY_VIEW_PIN_PATH} loader={viewPinRedirectPageLoader} />
        </Route>

        {/* Public routes */}
        <Route
          path={SOCIAL_CONNECT_COMPLETE_PATH}
          element={<SocialMediaAuthComplete />}
        />
        <Route path={SIGN_IN_CALLBACK_URL} />

        {/* View pin redirects */}
        <Route
          path="*"
          loader={basePathLoader}
          errorElement={<PageNotFound />}
        />
      </Route>

      <Route
        path={VIEW_PIN_PATH}
        element={<Profile />}
        loader={viewPinPageLoader}
        errorElement={<PageNotFound />}
      />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  )
);

export const Root = () => (
  <AuthProvider>
    <AnalyticsProvider>
      <ErrorTrackingProvider appName="profile-app">
        <LaunchDarklyProvider>
          <MaintenanceGate>
            <QueryClientProvider client={queryClient}>
              <TranslationContextProvider>
                <Suspense fallback={<Spinner />}>
                  <RouterProvider router={router} />
                </Suspense>
              </TranslationContextProvider>
            </QueryClientProvider>
          </MaintenanceGate>
        </LaunchDarklyProvider>
      </ErrorTrackingProvider>
    </AnalyticsProvider>
  </AuthProvider>
);
